/**
 * DO NOT MODIFY THIS FILE (Unless you really really have to).
 *
 * The Design System team is working on standardizing theming within the
 * Designer (and Editor).
 *
 * We're going to provide clear boundaries, mechanisms, and guidelines
 * to help folks understand the space and to be able to make changes with
 * confidence.
 *
 * In the meantime, if you really have to make changes, please reach
 * out to us!
 *
 * @since 2022-02-07
 * @link #design-system (Slack)
 * @see https://webflow.atlassian.net/browse/PDTY-2171
 * @see https://docs.google.com/document/d/1OOHFaJhsxbJVqXT7Zxk_Z79yzvpeGiw1mMqXua3NMAI
 */

import {white, black, fade} from '@packages/utilities/inline-style-utils';

const d2Colors = {
  white: '#ffffff',
  whiteA7: 'rgba(255, 255, 255, 0.07)',
  whiteA10: 'rgba(255, 255, 255, 0.1)',
  whiteA13: 'rgba(255, 255, 255, 0.13)',
  whiteA16: 'rgba(255, 255, 255, 0.16)',
  whiteA19: 'rgba(255, 255, 255, 0.19)',
  whiteA22: 'rgba(255, 255, 255, 0.22)',
  whiteA25: 'rgba(255, 255, 255, 0.25)',
  whiteA30: 'rgba(255, 255, 255, 0.30)',
  whiteA40: 'rgba(255, 255, 255, 0.40)',
  whiteA50: 'rgba(255, 255, 255, 0.50)',
  whiteA60: 'rgba(255, 255, 255, 0.60)',
  whiteA67: 'rgba(255, 255, 255, 0.67)',
  whiteA70: 'rgba(255, 255, 255, 0.70)',
  whiteA80: 'rgba(255, 255, 255, 0.80)',
  black: '#000000',
  blackA7: 'rgba(0, 0, 0, 0.07)',
  blackA10: 'rgba(0, 0, 0, 0.1)',
  blackA13: 'rgba(0, 0, 0, 0.13)',
  blackA16: 'rgba(0, 0, 0, 0.16)',
  blackA19: 'rgba(0, 0, 0, 0.19)',
  blackA22: 'rgba(0, 0, 0, 0.22)',
  blackA25: 'rgba(0, 0, 0, 0.25)',
  blackA30: 'rgba(0, 0, 0, 0.30)',
  blackA40: 'rgba(0, 0, 0, 0.40)',
  blackA50: 'rgba(0, 0, 0, 0.50)',
  blackA60: 'rgba(0, 0, 0, 0.60)',
  blackA67: 'rgba(0, 0, 0, 0.67)',
  blackA70: 'rgba(0, 0, 0, 0.70)',
  blackA80: 'rgba(0, 0, 0, 0.80)',
  gray: {
    '0': '#f5f5f5',
    '100': '#e8e8e8',
    '200': '#e0e0e0',
    '300': '#dbdbdb',
    '400': '#c4c4c4',
    '500': '#a6a6a6',
    '525': '#898989',
    /**
     * gray550 = Temporary.
     * Added to help bridge light theme tokens until we can
     * consolidate and refine that theme.
     *
     * This was suggested by Sergie on 2023-11-20.
     *
     * @since 2023-11-20
     * @see https://webflow.atlassian.net/browse/PDTY-7008
     * @see https://docs.google.com/spreadsheets/d/1_rPB7izyAYR2YeAMeSOsND4JV-hb54byLQSHw5kjBc8/edit#gid=1945827712
     */
    '550': '#8a8a8a',
    '575': '#828282',
    '600': '#6d6d6d',
    /**
     * Looking to deprecate gray700.
     */
    '700': '#5e5e5e',
    '800': '#404040',
    /**
     * Looking to deprecate gray900.
     */
    '900': '#373737',
    '1000': '#353535',
    '1100': '#292929',
    '1200': '#1e1e1e',
  },
  blue: {
    '100': '#a7d1ff',
    '200': '#187cd9',
    '300': '#006acc',
    '400': '#007df0',
    '500': '#0C6AC0',
    '600': '#0A5599',
    '700': '#084278',
    '800': '#062F56',
    '900': '#042543',
    A10: 'rgba(0, 125, 240, 0.1)',
    A15: 'rgba(0, 125, 240, 0.15)',
    A20: 'rgba(0, 125, 240, 0.2)',
    A25: 'rgba(0, 125, 240, 0.25)',
    A30: 'rgba(0, 125, 240, 0.3)',
    A35: 'rgba(0, 125, 240, 0.35)',
  },
  brandBlue: {
    '100': '#2C80FD',
    '200': '#146EF5',
    '300': '#1961F0',
    '400': '#1755E0',
    '500': '#144AC2',
    '600': '#113DA2',
    '700': '#0F348A',
    '800': '#0D2F7C',
    '900': '#0B2666',
    A10: 'rgba(20, 110, 245, 0.1)',
    A15: 'rgba(20, 110, 245, 0.15)',
    A20: 'rgba(20, 110, 245, 0.2)',
    A25: 'rgba(20, 110, 245, 0.25)',
    A30: 'rgba(20, 110, 245, 0.3)',
    A35: 'rgba(20, 110, 245, 0.35)',
  },
  green: {
    '100': '#79e09c',
    '200': '#0d8a4f',
    '300': '#007a41',
    '400': '#259d4d',
    '500': '#1D7C3D',
    '600': '#186733',
    '700': '#124F27',
    '800': '#0E3E1F',
    '900': '#0A2E17',
    A10: 'rgba(37, 157, 77, 0.1)',
    A15: 'rgba(37, 157, 77, 0.15)',
    A20: 'rgba(37, 157, 77, 0.2)',
    A25: 'rgba(37, 157, 77, 0.25)',
    A30: 'rgba(37, 157, 77, 0.3)',
    A35: 'rgba(37, 157, 77, 0.35)',
  },
  yellow: {
    '100': '#FFDE5B',
    '200': '#F0C000',
    '300': '#8B6D00',
    '400': '#FFD301',
    '500': '#CCA900',
    '600': '#A38700',
    '700': '#806900',
    '800': '#665400',
    '900': '#524300',
    A10: 'rgba(255, 211, 1, 0.1)',
    A15: 'rgba(255, 211, 1, 0.15)',
    A20: 'rgba(255, 211, 1, 0.2)',
    A25: 'rgba(255, 211, 1, 0.25)',
    A30: 'rgba(255, 211, 1, 0.3)',
    A35: 'rgba(255, 211, 1, 0.35)',
  },
  red: {
    '100': '#ff9c9c',
    '200': '#cb3535',
    '300': '#cf313b',
    '400': '#e42f3a',
    '500': '#AE2831',
    '600': '#912129',
    '700': '#781C22',
    '800': '#5B151A',
    '900': '#4B1115',
    A10: 'rgba(228, 47, 58, 0.1)',
    A15: 'rgba(228, 47, 58, 0.15)',
    A20: 'rgba(228, 47, 58, 0.2)',
    A25: 'rgba(228, 47, 58, 0.25)',
    A30: 'rgba(228, 47, 58, 0.3)',
    A35: 'rgba(228, 47, 58, 0.35)',
  },
  orange: {
    '100': '#ffbc86',
    '200': '#c95616',
    '300': '#bf4707',
    '400': '#df640c',
    '500': '#B84F14',
    '600': '#984110',
    '700': '#6F2F0C',
    '800': '#572509',
    '900': '#451D07',
    A10: 'rgba(223, 100, 12, 0.1)',
    A15: 'rgba(223, 100, 12, 0.15)',
    A20: 'rgba(223, 100, 12, 0.2)',
    A25: 'rgba(223, 100, 12, 0.25)',
    A30: 'rgba(223, 100, 12, 0.3)',
    A35: 'rgba(223, 100, 12, 0.35)',
  },
  purple: {
    '100': '#c4afff',
    '200': '#815beb',
    '300': '#734ce0',
    '400': '#875ffd',
    '500': '#653CDD',
    '600': '#512EB8',
    '700': '#3D238B',
    '800': '#321D72',
    '900': '#241551',
    A10: 'rgba(135, 95, 253, 0.1)',
    A15: 'rgba(135, 95, 253, 0.15)',
    A20: 'rgba(135, 95, 253, 0.2)',
    A25: 'rgba(135, 95, 253, 0.25)',
    A30: 'rgba(135, 95, 253, 0.3)',
    A35: 'rgba(135, 95, 253, 0.35)',
  },
  pink: {
    '100': '#febdff',
    '200': '#c44aa9',
    '300': '#b53a9a',
    '400': '#dd2cac',
    '500': '#A6358D',
    '600': '#872B73',
    '700': '#742563',
    '800': '#5D1E4F',
    '900': '#49183E',
    A10: 'rgba(221, 44, 172, 0.1)',
    A15: 'rgba(221, 44, 172, 0.15)',
    A20: 'rgba(221, 44, 172, 0.2)',
    A25: 'rgba(221, 44, 172, 0.25)',
    A30: 'rgba(221, 44, 172, 0.3)',
    A35: 'rgba(221, 44, 172, 0.35)',
  },
} as const;

const colors = {
  whiteFade01: white(0.01) as string,
  whiteFade05: white(0.05) as string,
  whiteFade10: white(0.1) as string,
  whiteFade20: white(0.2) as string,
  whiteFade90: white(0.9) as string,

  blackFade05: black(0.05) as string,
  blackFade80: black(0.8) as string,
  blackFade30: black(0.3) as string,
  blackFade48: black(0.48) as string,

  redFade10: fade(0.1)('red') as string,
  blueFade10: fade(0.1)('blue') as string,

  black: 'rgba(0, 0, 0, 1)',
  white: 'rgba(255, 255, 255, 1)',

  gray25: '#f6f6f6', // Only used for light theme
  gray50: '#ebebeb',
  gray100: '#d9d9d9',
  gray200: '#ababab',
  gray300: '#757575',
  gray350: '#696969',
  gray400: '#5e5e5e',
  gray500: '#4d4d4d',
  gray600: '#404040',
  gray700: '#363636',
  gray800: '#2b2b2b',
  gray900: '#212121',

  blue100: '#8AC2FF', // foreground (dark)
  blue200: '#2496FF', // ui/icons (dark)
  blue300: '#0084FF', // canvas (dark)
  blue350: '#1280EE', // backgroundHover (dark)
  blue400: '#0073E6', // background (dark)
  blue500: '#0066FF', // foreground/background (light)
  blue600: '#005CE5', // backgroundHover (light)

  purple100: '#B89EFF', // foreground (dark)
  purple200: '#A484FF', // ui/icons (dark)
  purple300: '#8A61FF', // canvas, backgroundHover (dark)
  purple400: '#774FE8', // background (dark)
  purple500: '#7140FF', // foreground/background (light)
  purple600: '#663AE5', // backgroundHover (light)

  pink100: '#F7A9F9', // foreground (dark)
  pink200: '#F775D3', // ui/icons (dark)
  pink300: '#E148C0', // canvas (dark)
  pink350: '#D34EB7', // backgroundHover (dark)
  pink400: '#BE4AA5', // background (dark)
  pink500: '#D914AE', // foreground/background (light)
  pink600: '#C3129D', // backgroundHover (light)

  red100: '#FF8A8A', // foreground (dark)
  red200: '#FF424D', // ui/icons (dark)
  red300: '#ED273E', // canvas (dark)
  red350: '#DB434C', // backgroundHover (dark)
  red400: '#CF313B', // background (dark)
  red500: '#D50000', // foreground/background (light)
  red600: '#B50000', // backgroundHover (light)

  orange100: '#FFAB66', // foreground (dark)
  orange200: '#F67E28', // ui/icons (dark)
  orange300: '#F85900', // canvas (dark)
  orange350: '#DD7124', // backgroundHover (dark), backgroundHover (light)
  orange400: '#C75300', // background (dark),  background (light), foreground (light)

  yellow300: '#FFC700', // canvas, ui/icons, text, backgroundHover (dark)
  yellow400: '#EAA700', // backgroundHover (light), background (light)
  yellow500: '#996E00', // background (dark), foreground (light)

  green100: '#63D489', // foreground (dark)
  green200: '#2FC862', // ui/icons (dark)
  green300: '#00A457', // canvas (dark)
  green350: '#00944F', // backgroundHover (dark)
  green400: '#008547', // background (dark), foreground/background (light)
  green500: '#00713C', // backgroundHover (light)

  d2: d2Colors,
} as const;

export type D2Colors = typeof d2Colors;
export default {colors};
